import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';
import {
  removeAccess,
  clearAllCookies,
  getToken,
} from '../services/auth/access-service';
import notification from '../components/BrowserNotifications';
import { serverStatusEnum } from '../components/enums/serverStatusEnum';
import { socketUrl } from '../services/socket';

const WSContext = createContext(null);
const AUDIO_DISABLED_NOTICE_MARKER = '[AUDIO_DISABLED_DEVICE]';
const CLIENT_RECONNECT_NOTICE_GRACE_MS = 3000;
const isWsTruthy = (value) =>
  value === true || value === 'True' || value === 'true' || value === 1 || value === '1';

const getMessageText = (payload) =>
  payload?.message?.mensagem ||
  payload?.message?.message ||
  payload?.message ||
  payload?.mensage ||
  payload?.value ||
  '';

const getMessageChatId = (payload) =>
  payload?.message?.id ||
  payload?.id ||
  payload?.idCliente ||
  payload?.chatId ||
  null;

export function WSProvider({ children }) {
  const navigate = useNavigate();

  const [isOnline, setIsOnline] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [notifyNewMessage, setNotifyNewMessage] = useState(true);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [serverStatus, setServerStatus] = useState(serverStatusEnum.CONNECTING);
  const [respLogin, setRespLogin] = useState(null);
  const [myUser, setMyUser] = useState(null);
  const [atendentesOnline, setAtendentesOnline] = useState(() => {
    try {
      const atendentesSalvos = localStorage.getItem('atendentesOnline');
      return atendentesSalvos ? JSON.parse(atendentesSalvos) : [];
    } catch (error) {
      console.error('Erro ao carregar atendentes do localStorage:', error);
      return [];
    }
  });
  const [nextClient, setNextClient] = useState({ count: 0, clients: [] });
  const [serviceRespGetUser, setServiceRespGetUser] = useState(null);
  const [chats, setChats] = useState(() => {
    try {
      const chatsSalvos = localStorage.getItem('chatsAtivos');
      return chatsSalvos ? JSON.parse(chatsSalvos) : [];
    } catch (error) {
      console.error('Erro ao carregar chats do localStorage:', error);
      return [];
    }
  });
  const [newMessage, setNewMessage] = useState({});
  const [clientIsTyping, setClientIsTyping] = useState({});
  const [clientIsRecording, setClientIsRecording] = useState({});
  const [chatsEncerrados, setChatsEncerrados] = useState([]);
  const [reconnectCounter, setReconnectCounter] = useState(0);
  const [authToken, setAuthToken] = useState(() =>
    localStorage.getItem('authToken')
  );
  const [reconnectingClients, setReconnectingClients] = useState(new Set());
  const [reconnectCountdowns, setReconnectCountdowns] = useState(new Map());
  const [confirmationModalData, setConfirmationModalData] = useState(null);
  const [activeChats, setActiveChats] = useState([]);
  const [audioDisabledChats, setAudioDisabledChats] = useState(new Set());

  const chatsEncerradosRef = useRef(chatsEncerrados);
  const chatsRef = useRef(chats);
  const isIntentionalLogout = useRef(false);
  const reconnectNoticeTimersRef = useRef(new Map());
  const isAdmin = useMemo(() => !!myUser?.tipo, [myUser]);

  useEffect(() => {
    localStorage.setItem('atendentesOnline', JSON.stringify(atendentesOnline));
  }, [atendentesOnline]);

  useEffect(() => {
    localStorage.setItem('chatsAtivos', JSON.stringify(chats));
    chatsRef.current = chats;
  }, [chats]);

  const handleSetAuthToken = useCallback((token) => {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
    setAuthToken(token);
  }, []);

  const isValidLogin = () => navigate('/app/home', { replace: true });

  const removeChat = useCallback((chatId) => {
    if (!chatId) return;

    const idParaRemover = String(chatId);
    setChats((prevChats) =>
      prevChats.filter((chat) => String(chat.id) !== idParaRemover)
    );
  }, []);

  const clearPendingReconnectNotice = useCallback((chatId) => {
    if (!chatId) return;
    const key = String(chatId);
    const timerId = reconnectNoticeTimersRef.current.get(key);
    if (timerId) {
      clearTimeout(timerId);
      reconnectNoticeTimersRef.current.delete(key);
    }
  }, []);

  const addMessageChat = (message) => {
    if (!message || !message.id) {
      setNewMessage(message);
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(message.id)) || [];
      stored.push(message);
      localStorage.setItem(message.id, JSON.stringify(stored));
    } catch (error) {
      console.error(`Falha ao salvar mensagem para o chat ${message.id}:`, error);
    }
    setNewMessage(message);
  };

  const Reconnect = (estadoWs) => {
    localStorage.setItem('statusServer', JSON.stringify(estadoWs));
  };

  const marcarChatComoEncerrado = useCallback((chatId) => {
    if (!chatId) return;
    clearPendingReconnectNotice(chatId);

    setChatsEncerrados((currentEncerrados) => {
      if (currentEncerrados.includes(chatId)) {
        return currentEncerrados;
      }
      const newEncerrados = [...currentEncerrados, chatId];
      chatsEncerradosRef.current = newEncerrados;
      return newEncerrados;
    });

    setReconnectingClients((prevSet) => {
      const newSet = new Set(prevSet);
      newSet.delete(chatId);
      return newSet;
    });

    setReconnectCountdowns((prevMap) => {
      const newMap = new Map(prevMap);
      if (newMap.has(chatId)) {
        newMap.delete(chatId);
        return newMap;
      }
      return prevMap;
    });
  }, [clearPendingReconnectNotice]);

  useEffect(() => {
    const timerManager = setInterval(() => {
      setReconnectCountdowns((currentMap) => {
        if (currentMap.size === 0) return currentMap;

        const newMap = new Map(currentMap);
        let hasChanged = false;

        newMap.forEach((countdown, chatId) => {
          hasChanged = true;
          if (countdown > 0) {
            newMap.set(chatId, countdown - 1);
          }
        });

        return hasChanged ? newMap : currentMap;
      });
    }, 1000);

    return () => clearInterval(timerManager);
  }, []);

  useEffect(
    () => () => {
      reconnectNoticeTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      reconnectNoticeTimersRef.current.clear();
    },
    []
  );

  const handleAtendentesList = (respWS) => {
    const meuId = myUser?.id;
    const meuNome = myUser?.nomeUsuario?.toLowerCase?.();
    const isMesmoUsuario = (data) => {
      const nome = data?.nomeUsuario?.toLowerCase?.();
      return data.id === meuId || (meuNome && nome?.includes(meuNome));
    };
    const currentUser = respWS.userData.find(isMesmoUsuario);
    const outrosAtendentes = respWS.userData.filter((data) => !isMesmoUsuario(data));
    if (currentUser) setMyUser(currentUser);
    setAtendentesOnline(outrosAtendentes);
  };

  const handleUserQueue = (respWS) => {
    let dadosFilaNormalizados = { count: 0, clients: [] };

    if (respWS.clients && typeof respWS.count !== 'undefined') {
      const normalizedClients = (respWS.clients || []).map((client, index) => ({
        ...client,
        id: client.id || client.numSerie || `fila-${index}`,
        name: client.name || client.nome || 'Cliente',
        numSerie: client.numSerie || client.id || '',
      }));
      dadosFilaNormalizados = {
        count: respWS.count,
        clients: normalizedClients,
      };
    } else if (respWS.userQueue) {
      const count = parseInt(respWS.userQueue, 10) || 0;
      if (count > 0) {
        const queueClientId = respWS.idNextCli || 'queue-placeholder';
        dadosFilaNormalizados = {
          count,
          clients: [
            {
              id: queueClientId,
              name: respWS.nameNextCli || 'Cliente na fila',
              numSerie: respWS.idNextCli || queueClientId,
            },
          ],
        };
      }
    }

    setNextClient(dadosFilaNormalizados);
  };

  const handleActiveChatsList = (respWS) => {
    const activeChatsList = respWS.activeChats || [];
    setActiveChats(activeChatsList);

    const activeIds = new Set(
      activeChatsList
        .map((chat) => chat?.cliente?.id || chat?.id || chat?.chatId)
        .filter(Boolean)
        .map((id) => String(id))
    );

    setChats((prevChats) => {
      const staleChats = prevChats.filter((chat) => !activeIds.has(String(chat.id)));
      if (staleChats.length > 0) {
        setChatsEncerrados((currentEncerrados) => {
          const merged = new Set(currentEncerrados.map((id) => String(id)));
          staleChats.forEach((chat) => merged.add(String(chat.id)));
          const result = Array.from(merged);
          chatsEncerradosRef.current = result;
          return result;
        });
      }
      return prevChats.filter((chat) => activeIds.has(String(chat.id)));
    });
  };

  const handleGetUser = (respWS) => {
    const newChatId = respWS.id;
    setChatsEncerrados((currentEncerrados) => {
      const newEncerrados = currentEncerrados.filter((id) => id !== newChatId);
      chatsEncerradosRef.current = newEncerrados;
      return newEncerrados;
    });
    setReconnectingClients((prevSet) => {
      const newSet = new Set(prevSet);
      newSet.delete(newChatId);
      return newSet;
    });
    setReconnectCountdowns((prevMap) => {
      const newMap = new Map(prevMap);
      if (newMap.has(newChatId)) {
        newMap.delete(newChatId);
        return newMap;
      }
      return prevMap;
    });
    setServiceRespGetUser(respWS);
    setChats((prevChats) => {
      if (prevChats.some((chat) => chat.id === newChatId)) {
        return prevChats;
      }
      return [...prevChats, respWS];
    });
    const existingHistory = localStorage.getItem(newChatId);
    if (existingHistory === null) {
      localStorage.setItem(newChatId, JSON.stringify([]));
    }
  };

  const handleMessage = (respWS) => {
    const textoDaMensagem = getMessageText(respWS);
    const idDoChat = getMessageChatId(respWS);

    if (!idDoChat) return;
    if (
      typeof textoDaMensagem === 'string' &&
      textoDaMensagem.startsWith(AUDIO_DISABLED_NOTICE_MARKER)
    ) {
      setAudioDisabledChats((current) => {
        const next = new Set(current);
        next.add(String(idDoChat));
        return next;
      });
      return;
    }

    addMessageChat(respWS);

    const mensagemDeEncerramento =
      'O cliente finalizou este atendimento. Pedimos que encerre este chat.';

    if (
      respWS.mensagemTipo === 'SYSTEM_MESSAGE'
      && respWS.value?.includes('perdeu a conexÃ£o')
      && !chatsEncerradosRef.current.includes(idDoChat)
    ) {
      const chatId = String(idDoChat);
      if (!reconnectNoticeTimersRef.current.has(chatId)) {
        const timerId = setTimeout(() => {
          reconnectNoticeTimersRef.current.delete(chatId);
          if (chatsEncerradosRef.current.includes(idDoChat)) return;

          const disconnectedChat = chatsRef.current.find((c) => c.id === idDoChat);
          if (disconnectedChat) {
            notification({
              title: 'Conexao Perdida',
              body: `O cliente ${disconnectedChat.name || 'desconhecido'} perdeu a conexao. Aguardando...`,
            });
          }

          setReconnectingClients((prevSet) => new Set(prevSet).add(idDoChat));
          setReconnectCountdowns((prevMap) => new Map(prevMap).set(idDoChat, 60));
        }, CLIENT_RECONNECT_NOTICE_GRACE_MS);
        reconnectNoticeTimersRef.current.set(chatId, timerId);
      }
      return;
    }

    if (respWS.mensagemTipo === 'SYSTEM_MESSAGE' && respWS.value?.includes('reconectou-se')) {
      clearPendingReconnectNotice(idDoChat);
    }

    if (
      textoDaMensagem === mensagemDeEncerramento ||
      respWS.mensage === 'O cliente finalizou o atendimento.'
    ) {
      marcarChatComoEncerrado(idDoChat);
      setReconnectingClients((prevSet) => {
        const newSet = new Set(prevSet);
        newSet.delete(idDoChat);
        return newSet;
      });
    } else if (respWS.mensagemTipo === 'SYSTEM_MESSAGE') {
      if (respWS.action === 'REQUEST_EQUIPMENT_CONFIRMATION') {
        setConfirmationModalData({
          isOpen: true,
          chatId: respWS.id,
          clientName: respWS.data.name,
          equipmentName: respWS.data.tipoEquipamento,
        });
        return;
      }

      if (respWS.value?.includes('perdeu a conexão')) {
        if (!chatsEncerradosRef.current.includes(respWS.id)) {
          const disconnectedChat = chatsRef.current.find((c) => c.id === respWS.id);
          if (disconnectedChat) {
            notification({
              title: 'Conexão Perdida',
              body: `O cliente ${
                disconnectedChat.name || 'desconhecido'
              } perdeu a conexão. Aguardando...`,
            });
          }

          setReconnectingClients((prevSet) => new Set(prevSet).add(respWS.id));
          setReconnectCountdowns((prevMap) => new Map(prevMap).set(respWS.id, 60));
        }
      } else if (respWS.value?.includes('reconectou-se')) {
        const reconnectedChat = chatsRef.current.find((c) => c.id === respWS.id);
        if (reconnectedChat) {
          notification({
            title: 'Cliente Reconectado',
            body: `O cliente ${
              reconnectedChat.name || 'desconhecido'
            } se reconectou com sucesso!`,
          });
        }

        setReconnectingClients((prevSet) => {
          const newSet = new Set(prevSet);
          newSet.delete(respWS.id);
          return newSet;
        });
        setReconnectCountdowns((prevMap) => {
          const newMap = new Map(prevMap);
          if (newMap.has(respWS.id)) {
            newMap.delete(respWS.id);
            return newMap;
          }
          return prevMap;
        });
      } else if (respWS.value?.includes('foi encerrado')) {
        const closedChat = chatsRef.current.find((c) => c.id === respWS.id);
        if (closedChat) {
          notification({
            title: 'Chat Encerrado',
            body: `O chat com ${
              closedChat.name || 'um cliente'
            } foi encerrado por inatividade.`,
          });
        }
        marcarChatComoEncerrado(respWS.id);
        setReconnectingClients((prevSet) => {
          const newSet = new Set(prevSet);
          newSet.delete(respWS.id);
          return newSet;
        });
        setReconnectCountdowns((prevMap) => {
          const newMap = new Map(prevMap);
          if (newMap.has(respWS.id)) {
            newMap.delete(respWS.id);
            return newMap;
          }
          return prevMap;
        });
      } else if (respWS.value?.includes('encerrou este chat')) {
        const closedChat = chatsRef.current.find((c) => c.id === respWS.id);
        if (closedChat) {
          notification({
            title: 'Chat Encerrado pelo Cliente',
            body: `O cliente ${
              closedChat.name || 'desconhecido'
            } encerrou o atendimento.`,
          });
        }
        marcarChatComoEncerrado(respWS.id);
      }
    } else {
      // ✅ LÓGICA DE NOTIFICAÇÃO PARA MENSAGENS NORMAIS
      const chatInfo = chatsRef.current.find((c) => c.id === idDoChat);
      if (chatInfo) {
        notification({
          title: `Nova mensagem de ${chatInfo.name || 'Cliente'}`,
          body: textoDaMensagem,
        });
      }
    }
  };

  const handleReceiveFile = (respWS) => {
    addMessageChat({
      serviceResp: respWS.serviceResp,
      id: respWS.idCliente,
      userName: respWS.userName,
      filePath: respWS.filePath,
      replyTo: respWS.replyTo || null,
      data: respWS.data,
    });
  };

  const handleSuportTyping = (respWS) =>
    setClientIsTyping({
      ...respWS,
      typing: isWsTruthy(respWS?.typing),
    });
  const handleSuportRecording = (respWS) =>
    setClientIsRecording({
      ...respWS,
      recording: isWsTruthy(respWS?.recording),
    });

  const handleDisconnect = (respWS) => {
    clearPendingReconnectNotice(respWS.id);
    addMessageChat(respWS);
    const idDoChat = respWS.id;
    if (idDoChat) {
      marcarChatComoEncerrado(idDoChat);
      setAudioDisabledChats((current) => {
        const next = new Set(current);
        next.delete(String(idDoChat));
        return next;
      });
      removeChat(idDoChat);
    } else {
      console.warn(
        'Recebido evento de disconnect sem um ID de chat específico.',
        respWS
      );
    }
  };

  const { sendJsonMessage, getWebSocket } = useWebSocket(
    `${socketUrl}?reconnect=${reconnectCounter}`,
    {
      onOpen: (value) => {
        console.log('Connected to App WS');
        isIntentionalLogout.current = false;
        Reconnect(value.type);
        setServerStatus(serverStatusEnum.CONNECTED);
        setReconnectAttempts(0);
        setChatsEncerrados([]);
        setAudioDisabledChats(new Set());
        const token = getToken();
        if (token) {
          setIsLoggingIn(true);
          sendJsonMessage({ serviceType: 'login', token });
        }
      },
      onMessage: (MessageEvent) => {
        const respWS = JSON.parse(MessageEvent.data);
        if (respWS?.login === 'true') {
          setIsOnline(true);
          setRespLogin(respWS.login);
          setMyUser(respWS.userData);
          sendJsonMessage({ serviceType: 'solicitarFila' });
          sendJsonMessage({ serviceType: 'solicitarAtendimentosAtivos' });

          setTimeout(isValidLogin, 20);
          return;
        }
        if (respWS?.login === 'false') {
          setIsOnline(false);
          setIsLoggingIn(false);
          setRespLogin(respWS.error);
          handleSetAuthToken(null);
          return;
        }
        switch (respWS?.serviceResp) {
          case 'userList':
            handleAtendentesList(respWS);
            break;
          case 'userQueue':
            handleUserQueue(respWS);
            break;
          case 'activeChatsList':
            handleActiveChatsList(respWS);
            break;
          case 'getUser':
            handleGetUser(respWS);
            break;
          case 'mensage':
          case 'message':
            handleMessage(respWS);
            break;
          case 'receiveFile':
            handleReceiveFile(respWS);
            break;
          case 'suportTyping':
            handleSuportTyping(respWS);
            break;
          case 'suportRecording':
            handleSuportRecording(respWS);
            break;
          case 'disconnect':
            handleDisconnect(respWS);
            break;
          case 'forceDisconnect':
            console.warn(
              `[FORCE DISCONNECT] Recebido comando para encerrar o chat ${respWS.id}. Motivo: ${respWS.reason}`
            );
            marcarChatComoEncerrado(respWS.id);
            setTimeout(() => {
              removeChat(respWS.id);
            }, 4000);
            break;
          default:
            console.warn('Mensagem desconhecida recebida:', respWS);
        }
      },
      onError: (event) => {
        console.error('WebSocket Error:', event);
        setServerStatus(serverStatusEnum.ERROR);
      },
      onClose: () => {
        console.log('WS Close');
        if (!isIntentionalLogout.current) {
          notification({
            title: 'você foi desconectado',
            body: 'Você foi desconectado. Por favor, Realize o login novamente',
          });
        }
        setServerStatus(serverStatusEnum.DISCONNECTED);
        setIsOnline(false);
      },
      shouldReconnect: () => {
        setReconnectAttempts((prev) => prev + 1);
        if (reconnectAttempts >= 20) {
          setServerStatus(serverStatusEnum.ERROR);
          return false;
        }
        setServerStatus(serverStatusEnum.RECONNECTING);
        return true;
      },
      reconnectInterval: 3000,
    }
  );

  const login = useCallback(
    (token) => {
      if (!token) {
        console.error('Tentativa de login no WebSocket sem um token.');
        return;
      }
      setIsLoggingIn(true);
      handleSetAuthToken(token);
      sendJsonMessage({ serviceType: 'login', token });
    },
    [handleSetAuthToken, sendJsonMessage]
  );

  const logout = useCallback(() => {
    const socket = getWebSocket();

    if (socket && socket.readyState === WebSocket.OPEN) {
      sendJsonMessage({ serviceType: 'logout' });
    }

    removeAccess();
    clearAllCookies();
    localStorage.clear();
    sessionStorage.clear();
    handleSetAuthToken(null);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.close(1000, 'Logout intencional do usuário');
    }

    setIsOnline(false);
    setIsLoggingIn(false);
    setRespLogin(null);
    setMyUser(null);
    setAtendentesOnline([]);
    setChats([]);
    setNewMessage({});
    setClientIsTyping({});
    setClientIsRecording({});
    setChatsEncerrados([]);
    chatsEncerradosRef.current = [];
    setAudioDisabledChats(new Set());
    setReconnectingClients(new Set());
    setReconnectCountdowns(new Map());
    reconnectNoticeTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    reconnectNoticeTimersRef.current.clear();
  }, [handleSetAuthToken, getWebSocket, sendJsonMessage]);

  const returnLogin = () => navigate('/app/home', { replace: true });

  const handleConfirmarEncerramento = useCallback(
    (chatId, equipamentoConfirmado) => {
      if (!chatId || !equipamentoConfirmado) {
        console.error(
          'É necessário ID do chat e nome do equipamento para confirmar.'
        );
        return;
      }
      sendJsonMessage({
        serviceType: 'confirmarEncerramento',
        id: chatId,
        equipamentoConfirmado,
      });
      setConfirmationModalData(null);
    },
    [sendJsonMessage]
  );

  const handleCancelarEncerramento = useCallback(() => {
    setConfirmationModalData(null);
  }, []);

  const value = useMemo(
    () => ({
      login,
      logout,
      returnLogin,
      handleSendMessageWS: sendJsonMessage,
      marcarChatComoEncerrado,
      serverStatus,
      isOnline,
      isAdmin,
      isLoggingIn,
      respLogin,
      myUser,
      atendentesOnline,
      nextClient,
      activeChats,
      audioDisabledChats,
      serviceRespGetUser,
      newMessage,
      clientIsTyping,
      clientIsRecording,
      chats,
      removeChat,
      chatsEncerrados,
      reconnectingClients,
      reconnectCountdowns,
      setChats,
      setRespLogin,
      confirmationModalData,
      handleConfirmarEncerramento,
      handleCancelarEncerramento,
      setNewMessage,
    }),
    [
      login,
      logout,
      returnLogin,
      sendJsonMessage,
      marcarChatComoEncerrado,
      serverStatus,
      isOnline,
      isAdmin,
      isLoggingIn,
      respLogin,
      myUser,
      atendentesOnline,
      nextClient,
      activeChats,
      audioDisabledChats,
      serviceRespGetUser,
      newMessage,
      clientIsTyping,
      clientIsRecording,
      chats,
      removeChat,
      chatsEncerrados,
      reconnectingClients,
      reconnectCountdowns,
      confirmationModalData,
      handleConfirmarEncerramento,
      handleCancelarEncerramento,
      setNewMessage,
    ]
  );

  return <WSContext.Provider value={value}>{children}</WSContext.Provider>;
}

export const useWSContext = () => {
  const context = useContext(WSContext);
  if (!context) {
    throw new Error('useWSContext deve ser usado dentro de um WSProvider');
  }
  return context;
};
